import type { ReactNode } from 'react';

import type { DestinationId } from './navigation';

const accent = 'fo-nav-glyph__accent';
const soft = 'fo-nav-glyph__soft';
const cutout = 'fo-nav-glyph__cutout';
const lightStroke = 'fo-nav-glyph__light-stroke';

/**
 * One drawn glyph per destination, on a 32 grid at a 1.7 stroke.
 *
 * Keyed by destination id with every id required, so a destination cannot reach the rail without
 * its own drawing: there is no fallback glyph to hide behind.
 */
const GLYPHS: Record<DestinationId, ReactNode> = {
  today: (
    <>
      <rect className={soft} x="5" y="2.5" width="22" height="27" rx="4.5" />
      <rect className={accent} x="9" y="7.5" width="5.5" height="5.5" rx="1.6" />
      <path className={lightStroke} d="m10.2 10.3 1.4 1.4 2.2-2.6" />
      <rect x="9" y="14.5" width="5.5" height="5.5" rx="1.6" />
      <rect x="9" y="21.5" width="5.5" height="5.5" rx="1.6" />
      <path d="M18 10.3h5m-5 7h5m-5 7h5" />
    </>
  ),
  field: (
    <>
      <rect className={soft} x="8" y="2" width="16" height="28" rx="4" />
      <path d="M13 5h6m-5 21h4" />
      <path className={accent} d="M21 13a5 5 0 0 0-10 0c0 4 5 9 5 9s5-5 5-9Z" />
      <circle className={cutout} cx="16" cy="13" r="1.5" />
    </>
  ),
  schedule: (
    <>
      <rect className={soft} x="3" y="5" width="21" height="20" rx="4" />
      <path d="M9 3v5m9-5v5M3 12h21" />
      <circle className={accent} cx="22.5" cy="22.5" r="7.5" />
      <path className={lightStroke} d="M22.5 18v4.8l3.2 1.9" />
    </>
  ),
  customers: (
    <>
      <circle className={soft} cx="23" cy="11" r="4" />
      <path className={soft} d="M18 27v-4a6 6 0 0 1 12 0v4Z" />
      <circle className={accent} cx="12" cy="10" r="5" />
      <path d="M3 28v-3a9 9 0 0 1 18 0v3Z" />
    </>
  ),
  sales: (
    <>
      <circle className={soft} cx="14.5" cy="17.5" r="11.5" />
      <circle cx="14.5" cy="17.5" r="6.4" />
      <circle className={accent} cx="14.5" cy="17.5" r="2.5" />
      <path d="M16.3 15.7 27 5" />
      <path d="M23.2 8.8V4.4m0 4.4h4.4" />
    </>
  ),
  // Two piles, never one cylinder: a single stack of discs is what a database looks like.
  finance: (
    <>
      <path className={soft} d="M3 11v12.6c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V11" />
      <path d="M3 15.2c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4M3 19.4c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4" />
      <ellipse className={accent} cx="9" cy="11" rx="6" ry="2.4" />
      <ellipse className={lightStroke} cx="9" cy="11" rx="2.6" ry=".9" />
      <path className={soft} d="M17 17.5v8.4c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4v-8.4" />
      <path d="M17 21.7c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4" />
      <ellipse className={soft} cx="23" cy="17.5" rx="6" ry="2.4" />
      <ellipse cx="23" cy="17.5" rx="2.6" ry=".9" />
    </>
  ),
  pricebook: (
    <>
      <path className={soft} d="M6 4h16a3 3 0 0 1 3 3v21H8a4 4 0 0 1-4-4V6a2 2 0 0 1 2-2Z" />
      <path d="M4 24a4 4 0 0 1 4-4h17M10 4v16" />
      <path className={accent} d="m17 8 5 5-5 5-5-5Z" />
      <circle className={cutout} cx="17" cy="11.5" r=".7" />
    </>
  ),
  reports: (
    <>
      <path d="M4 4v24h25" />
      <rect className={soft} x="8" y="17" width="5" height="7" rx="1.5" />
      <rect className={soft} x="16" y="12" width="5" height="12" rx="1.5" />
      <rect className={accent} x="24" y="5" width="5" height="19" rx="1.5" />
      <path d="m8 11 8-7 5 1" />
    </>
  ),
  team: (
    <>
      <circle className={soft} cx="6" cy="12" r="3" />
      <circle className={soft} cx="26" cy="12" r="3" />
      <path d="M1 26v-3a5 5 0 0 1 9-3m21 6v-3a5 5 0 0 0-9-3" />
      <circle className={accent} cx="16" cy="8" r="4.5" />
      <path className={soft} d="M8 28v-4a8 8 0 0 1 16 0v4Z" />
    </>
  ),
  settings: (
    <>
      <path
        className={soft}
        d="m13 3 6 0 1 4 4 2 4-1 3 5-3 3v4l3 3-3 5-4-1-4 2-1 4h-6l-1-4-4-2-4 1-3-5 3-3v-4l-3-3 3-5 4 1 4-2Z"
        transform="translate(1 -1) scale(.94)"
      />
      <circle className={accent} cx="16" cy="16" r="5.5" />
      <circle className={cutout} cx="16" cy="16" r="2" />
    </>
  ),
};

/**
 * The glyph a destination wears in the rail.
 *
 * Navigation symbols have a fuller silhouette than the compact in-record controls: two tones on a
 * tile, drawn to stay distinct from each other at 27 pixels.
 *
 * @param props.id the destination whose glyph to draw
 * @returns the decorative glyph; the link's text is its name
 */
export function NavigationIcon({ id }: { readonly id: DestinationId }): React.JSX.Element {
  return (
    <span className="fo-nav-icon" aria-hidden="true">
      <svg
        className="fo-nav-glyph"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        focusable="false"
      >
        {GLYPHS[id]}
      </svg>
    </span>
  );
}
